import { CommonModule, formatDate } from '@angular/common';
import { Component, EventEmitter, forwardRef, Input, OnChanges, Output, SimpleChanges } from '@angular/core';
import { ControlValueAccessor, NG_VALUE_ACCESSOR } from '@angular/forms';
import { NgbDateStruct } from '@ng-bootstrap/ng-bootstrap';

/** A single cell in the calendar grid: a day of the displayed month, or `null` for a blank padding cell. */
export type DayCell = NgbDateStruct | null;

/**
 * A contiguous date range, the value shape when the picker selects a range (see `DatePickerComponent.isRange`).
 *
 * `from` is the (inclusive) start and `to` the (inclusive) end. While a range is being
 * staged, `to` may be `null` (only the start has been picked). A committed range always
 * satisfies `from <= to`.
 */
export interface DateRange {
  /** Inclusive start of the range, or `null` when nothing is selected. */
  from: NgbDateStruct | null;
  /** Inclusive end of the range, or `null` when only the start has been picked. */
  to: NgbDateStruct | null;
}

/**
 * The value shape understood by the picker:
 * - single date (`mode="single"` without `range`) → `NgbDateStruct | null`
 * - range (`mode="multiple"`, or `mode="single"` with `range`) → `DateRange | null`
 */
export type DatePickerValue = NgbDateStruct | DateRange | null;

/**
 * `app-date-picker` — a reusable, inline calendar panel supporting single-date and range selection.
 *
 * Renders just the calendar card (header, weekday row, day grid, Apply/Clear footer);
 * callers drop it into a dropdown/overlay as needed. Uses `NgbDateStruct`
 * (`{ year, month, day }`) as its underlying date model to match the rest of the app.
 *
 * The `mode` input picks the layout: `'single'` (default) shows one calendar, `'multiple'`
 * shows two independent month calendars (From and To) and always selects a range. A
 * `'single'` picker selects one date unless the `range` input is set, in which case it
 * selects a range within its one calendar.
 * Selection is staged in every case: clicking highlights a pending selection, and the value
 * is only committed (and emitted) when Apply is pressed. Clear resets the pending selection
 * and emits `clear`.
 *
 * Implements `ControlValueAccessor` so it works with `[(ngModel)]` and reactive forms.
 *
 * @example
 * ```html
 * <!-- single (default) -->
 * <app-date-picker
 *   [(ngModel)]="date"
 *   [minDate]="{ year: 2026, month: 1, day: 1 }"
 *   (apply)="onApply($event)">
 * </app-date-picker>
 *
 * <!-- single calendar, range selection -->
 * <app-date-picker
 *   [range]="true"
 *   [(ngModel)]="range"
 *   (apply)="onApplyRange($event)">
 * </app-date-picker>
 *
 * <!-- two calendars, always a range -->
 * <app-date-picker
 *   mode="multiple"
 *   [(ngModel)]="range"
 *   (apply)="onApplyRange($event)">
 * </app-date-picker>
 * ```
 */
@Component({
  selector: 'app-date-picker',
  standalone: true,
  imports: [
    CommonModule,
  ],
  templateUrl: './date-picker.component.html',
  styleUrl: './date-picker.component.scss',
  providers: [
    {
      provide: NG_VALUE_ACCESSOR,
      useExisting: forwardRef(() => DatePickerComponent),
      multi: true,
    },
  ],
})
export class DatePickerComponent implements ControlValueAccessor, OnChanges {
  /**
   * Calendar layout. `'single'` (default) shows one calendar; `'multiple'` shows two
   * side-by-side calendars and is always a range picker (the value is a {@link DateRange}).
   *
   * In `'single'` mode the picker selects one date unless {@link range} is set.
   *
   * Treated as set-once: changing it at runtime after a value has been set is not supported
   * and may leave the pending selection in an inconsistent shape.
   */
  @Input() mode: 'single' | 'multiple' = 'single';

  /**
   * Lets a `'single'` (one-calendar) picker select a range instead of one date.
   * Ignored in `'multiple'` mode, which is always a range. Same set-once caveat as {@link mode}.
   */
  @Input() range = false;

  /** The committed selected value. Also settable via `ControlValueAccessor.writeValue`. Shape follows {@link isRange}. */
  @Input() value: DatePickerValue = null;

  /**
   * Baseline restored by Reset. Falls back to {@link value} when omitted
   * (undo in-progress picks rather than a separate factory default).
   */
  @Input() defaultValue: DatePickerValue = null;

  /** Earliest selectable date (inclusive). Days before it render dimmed and are non-selectable. */
  @Input() minDate?: NgbDateStruct;

  /** Latest selectable date (inclusive). Days after it render dimmed and are non-selectable. */
  @Input() maxDate?: NgbDateStruct;

  /** Extra per-day rule (like ng-bootstrap's `markDisabled`): return `true` to make a day non-selectable, e.g. weekends. */
  @Input() markDisabled?: (date: NgbDateStruct) => boolean;

  /** Emitted with the committed value when Apply is pressed (keeps `ngModel` in sync via the CVA). Shape follows {@link isRange}. */
  @Output() valueChange = new EventEmitter<DatePickerValue>();

  /** Emitted when Apply is clicked, carrying the committed value. Shape follows {@link isRange}. */
  @Output() apply = new EventEmitter<DatePickerValue>();

  /** Emitted when Clear is clicked. */
  @Output() clear = new EventEmitter<void>();

  /** Emitted when Reset restores the baseline ({@link defaultValue} or {@link value}). */
  @Output() reset = new EventEmitter<DatePickerValue>();

  /** Pending (not-yet-committed) single-date selection, highlighted in the grid. */
  pending: NgbDateStruct | null = null;

  /** Pending (not-yet-committed) range selection, highlighted in the grid. */
  pendingRange: DateRange = { from: null, to: null };

  /** 1-based month currently displayed in the left / single calendar. */
  displayMonth = new Date().getMonth() + 1;

  /** Year currently displayed in the left / single calendar. */
  displayYear = new Date().getFullYear();

  /** Independent right-hand month in `'multiple'` mode (not tied to the left calendar). */
  rightDisplayMonth = this.displayMonth === 12 ? 1 : this.displayMonth + 1;

  /** Independent right-hand year in `'multiple'` mode. */
  rightDisplayYear = this.displayMonth === 12 ? this.displayYear + 1 : this.displayYear;

  /** Weekday column headers (week starts Sunday). */
  readonly weekdays: readonly string[] = [
    'Su',
    'Mo',
    'Tu',
    'We',
    'Th',
    'Fr',
    'Sa',
  ];

  /** Month names for the header jump menu (1-based value). */
  readonly monthOptions = Array.from({ length: 12 }, (_, i) => ({
    value: i + 1,
    label: formatDate(new Date(2000, i, 1), 'MMMM', 'en-US'),
    short: formatDate(new Date(2000, i, 1), 'MMM', 'en-US'),
  }));

  /** Open month/year menu, or `null` when closed. */
  jumpMenu: { kind: 'month' | 'year'; side: 'left' | 'right' } | null = null;

  private onChange: (value: DatePickerValue) => void = () => {};
  private onTouched: () => void = () => {};
  private disabledByForm = false;

  ngOnChanges(changes: SimpleChanges): void {
    if (changes['value']) {
      this.syncFromValue(this.value);
    }
  }

  /** Whether the picker selects a range (`'multiple'` mode, or `'single'` with {@link range}). */
  get isRange(): boolean {
    return this.mode === 'multiple' || this.range;
  }

  // ── ControlValueAccessor ──────────────────────────────────────────────

  writeValue(value: DatePickerValue): void {
    this.value = value ?? null;
    this.syncFromValue(this.value);
  }

  /** Discards the uncommitted pending selection, restoring it to the committed {@link value}. */
  revertToValue(): void {
    this.jumpMenu = null;
    this.syncFromValue(this.value);
  }

  registerOnChange(fn: (value: DatePickerValue) => void): void {
    this.onChange = fn;
  }

  registerOnTouched(fn: () => void): void {
    this.onTouched = fn;
  }

  setDisabledState(isDisabled: boolean): void {
    this.disabledByForm = isDisabled;
  }

  // ── Grid construction ─────────────────────────────────────────────────

  /**
   * Builds the (left) calendar as an array of weeks; each week is 7 cells. Leading
   * cells before day 1 are `null` so the first day lands under the correct weekday.
   */
  get weeks(): DayCell[][] {
    return this.buildWeeks(this.displayYear, this.displayMonth);
  }

  /** Human-readable label for the left/single header, e.g. "March 2026". */
  get monthLabel(): string {
    return this.formatMonthLabel(this.displayYear, this.displayMonth);
  }

  /** Right-hand month in `'multiple'` mode (the month after {@link displayMonth}). */
  get adjacentWeeks(): DayCell[][] {
    const { year, month } = this.adjacentMonth;
    return this.buildWeeks(year, month);
  }

  /** Header label for the right-hand calendar, e.g. "September 2026". */
  get adjacentMonthLabel(): string {
    const { year, month } = this.adjacentMonth;
    return this.formatMonthLabel(year, month);
  }

  /** Years offered in the jump menu, bounded by min/max when set. */
  get years(): number[] {
    const now = new Date().getFullYear();
    const start = this.minDate?.year ?? now - 15;
    // No maxDate means an open-ended planner (future years), not "through this year".
    const end = this.maxDate?.year ?? now + 15;
    const years: number[] = [];
    for (let year = start; year <= end; year++) {
      years.push(year);
    }
    return years;
  }

  /**
   * The pending selection formatted for the footer. Empty when nothing is selected.
   * - single date: `"16/05/2026"`
   * - range: `"16/05/2026 - 23/05/2026"`, or just the start when only the start is picked.
   */
  get footerLabel(): string {
    if (this.isRange) {
      const { from, to } = this.pendingRange;
      if (!from) {
        return '';
      }
      return to ? `${this.formatDate(from)} - ${this.formatDate(to)}` : this.formatDate(from);
    }
    return this.pending ? this.formatDate(this.pending) : '';
  }

  /**
   * Whether Apply should be disabled: the pending selection is incomplete.
   * A range requires BOTH `from` and `to`; a single date requires a date.
   */
  get isApplyDisabled(): boolean {
    return this.isRange ? !this.pendingRange.from || !this.pendingRange.to : !this.pending;
  }

  // ── Interaction ───────────────────────────────────────────────────────

  /** Moves one calendar back a month. Defaults to the left / single calendar. */
  prevMonth(side: 'left' | 'right' = 'left'): void {
    this.shiftDisplayedMonth(side, -1);
  }

  /** Moves one calendar forward a month. Defaults to the left / single calendar. */
  nextMonth(side: 'left' | 'right' = 'left'): void {
    this.shiftDisplayedMonth(side, 1);
  }

  monthName(month: number): string {
    return this.monthOptions[month - 1]?.label ?? '';
  }

  isJumpOpen(kind: 'month' | 'year', side: 'left' | 'right'): boolean {
    return this.jumpMenu?.kind === kind && this.jumpMenu.side === side;
  }

  toggleJump(kind: 'month' | 'year', side: 'left' | 'right'): void {
    this.jumpMenu = this.isJumpOpen(kind, side) ? null : { kind, side };
  }

  pickMonth(month: number, side: 'left' | 'right'): void {
    const year = side === 'left' ? this.displayYear : this.adjacentMonth.year;
    this.jumpTo(year, month, side);
    this.jumpMenu = null;
  }

  pickYear(year: number, side: 'left' | 'right'): void {
    const month = side === 'left' ? this.displayMonth : this.adjacentMonth.month;
    this.jumpTo(year, month, side);
    this.jumpMenu = null;
  }

  isMonthDisabled(month: number, year: number): boolean {
    if (this.minDate && (year < this.minDate.year || (year === this.minDate.year && month < this.minDate.month))) {
      return true;
    }
    if (this.maxDate && (year > this.maxDate.year || (year === this.maxDate.year && month > this.maxDate.month))) {
      return true;
    }
    return false;
  }

  /**
   * Stages a day as the pending selection. No-op for blank or disabled cells.
   *
   * When selecting a range:
   * - first click sets `from` (and clears `to`);
   * - second click sets the other endpoint; if it lands before `from` the two
   *   are swapped so `from <= to` always holds;
   * - a third click (both already set) starts a fresh range from the clicked day.
   */
  selectDay(day: DayCell): void {
    if (!day || this.isDisabled(day) || this.disabledByForm) {
      return;
    }

    this.jumpMenu = null;

    if (this.isRange) {
      const { from, to } = this.pendingRange;
      if (!from || to) {
        // Start (or restart) a range.
        this.pendingRange = { from: day, to: null };
      } else if (this.toComparable(day) < this.toComparable(from)) {
        // Second pick lands before the start: swap so `from <= to` always holds.
        this.pendingRange = { from: day, to: from };
      } else {
        this.pendingRange = { from, to: day };
      }
      this.onTouched();
      return;
    }

    this.pending = day;
    this.onTouched();
  }

  /** Commits the pending selection: updates the value and emits `valueChange`/`apply` with the mode-appropriate shape. */
  applySelection(): void {
    this.value = this.isRange ? { ...this.pendingRange } : this.pending;
    this.onChange(this.value);
    this.valueChange.emit(this.value);
    this.apply.emit(this.value);
  }

  /** Clears the pending selection and emits `clear`. */
  clearSelection(): void {
    if (this.isRange) {
      this.pendingRange = { from: null, to: null };
    } else {
      this.pending = null;
    }
    this.onTouched();
    this.clear.emit();
  }

  /** Restores the pending selection to {@link defaultValue} (or {@link value}) and commits it. */
  resetSelection(): void {
    this.jumpMenu = null;
    const baseline = this.defaultValue ?? this.value ?? null;
    this.syncFromValue(baseline);
    this.onTouched();
    this.reset.emit(baseline);
    if (!this.isApplyDisabled) {
      this.applySelection();
    }
  }

  // ── Cell state helpers (used by the template) ─────────────────────────

  /**
   * Whether a day is non-selectable. A day is disabled only when it falls
   * outside `[minDate, maxDate]` or is rejected by {@link markDisabled}. When
   * selecting a range the two endpoints may be picked in either order — an
   * out-of-order second pick is swapped in {@link selectDay}, not blocked here.
   */
  isDisabled(day: DayCell): boolean {
    if (!day) {
      return true;
    }
    const value = this.toComparable(day);
    if (this.minDate && value < this.toComparable(this.minDate)) {
      return true;
    }
    if (this.maxDate && value > this.toComparable(this.maxDate)) {
      return true;
    }
    // A range allows the two endpoints to be picked in any order: a second
    // pick before the start is swapped in `selectDay` rather than blocked here.
    return !!this.markDisabled?.(day);
  }

  /**
   * Whether a day falls after `maxDate` and therefore represents a future /
   * "no data yet" day. These days are already disabled by `isDisabled`; this
   * flags them so the template can layer a distinct shade that reads as
   * "unavailable — can't pick", separate from the generic dimmed style.
   */
  isBeyondMax(day: DayCell): boolean {
    if (!day) {
      return false;
    }
    if (this.maxDate && this.toComparable(day) > this.toComparable(this.maxDate)) {
      return true;
    }
    return false;
  }

  /** Whether a day is the current pending selection (single mode). */
  isSelected(day: DayCell): boolean {
    return !!day && !!this.pending && this.equals(day, this.pending);
  }

  /** Whether a day is the pending range start (range selection). */
  isRangeStart(day: DayCell): boolean {
    return !!day && !!this.pendingRange.from && this.equals(day, this.pendingRange.from);
  }

  /** Whether a day is the pending range end (range selection). Only meaningful once `to` is picked. */
  isRangeEnd(day: DayCell): boolean {
    return !!day && !!this.pendingRange.to && this.equals(day, this.pendingRange.to);
  }

  /**
   * Whether a day is the pending range start while no end has been picked yet.
   * Such a cell renders fully rounded (like the single-mode selected cell) rather
   * than left-rounded, since there is no band to connect to.
   */
  isRangeSingle(day: DayCell): boolean {
    return !this.pendingRange.to && this.isRangeStart(day);
  }

  /**
   * Whether a day is strictly between the pending range's `from` and `to`
   * (i.e. covered by the band but not an endpoint). Returns `false` until both ends are set.
   */
  isInRange(day: DayCell): boolean {
    const { from, to } = this.pendingRange;
    if (!day || !from || !to) {
      return false;
    }
    const value = this.toComparable(day);
    return value > this.toComparable(from) && value < this.toComparable(to);
  }

  /**
   * Whether a day falls OUTSIDE the pending range (range selection only) — i.e.
   * strictly before `from` or strictly after `to`. Such days render with
   * de-emphasised text so the selected window stands out; they remain
   * selectable (unless separately blocked by `maxDate`), this is a purely
   * visual mute. Returns `false` in single mode, for null cells, for days
   * within the range, and — until an end (`to`) is picked — for days after the
   * start (only days before a lone `from` are muted at that point).
   */
  isOutsideRange(day: DayCell): boolean {
    if (!this.isRange || !day) {
      return false;
    }
    const { from, to } = this.pendingRange;
    const value = this.toComparable(day);
    if (from && value < this.toComparable(from)) {
      return true;
    }
    if (to && value > this.toComparable(to)) {
      return true;
    }
    return false;
  }

  /** Whether a day should render the small orange dot: single selected day, range start, or range end. */
  hasDot(day: DayCell): boolean {
    if (this.isRange) {
      return this.isRangeStart(day) || this.isRangeEnd(day);
    }
    return this.isSelected(day);
  }

  /** Whether a day conveys `aria-selected`: single selected day, or a range endpoint. */
  isAriaSelected(day: DayCell): boolean {
    if (this.isRange) {
      return this.isRangeStart(day) || this.isRangeEnd(day);
    }
    return this.isSelected(day);
  }

  /** Accessible label for a day cell, e.g. "16 May 2026". */
  dayAriaLabel(day: DayCell): string {
    return day ? this.formatDate(day) : '';
  }

  // ── Internals ─────────────────────────────────────────────────────────

  private syncFromValue(value: DatePickerValue): void {
    if (this.isRange) {
      const range = (value as DateRange | null) ?? { from: null, to: null };
      this.pendingRange = { from: range.from ?? null, to: range.to ?? null };
      if (this.pendingRange.from) {
        this.displayMonth = this.pendingRange.from.month;
        this.displayYear = this.pendingRange.from.year;
        if (this.pendingRange.to) {
          this.rightDisplayMonth = this.pendingRange.to.month;
          this.rightDisplayYear = this.pendingRange.to.year;
        } else {
          const next = this.shiftedMonth(this.displayYear, this.displayMonth, 1);
          this.rightDisplayYear = next.year;
          this.rightDisplayMonth = next.month;
        }
      }
      return;
    }

    const single = (value as NgbDateStruct | null) ?? null;
    this.pending = single;
    if (single) {
      this.displayMonth = single.month;
      this.displayYear = single.year;
    }
  }

  /** Jump one calendar to `year`/`month` without moving the other. */
  private jumpTo(year: number, month: number, side: 'left' | 'right'): void {
    if (side === 'right') {
      this.rightDisplayYear = year;
      this.rightDisplayMonth = month;
      return;
    }
    this.displayYear = year;
    this.displayMonth = month;
  }

  private shiftDisplayedMonth(side: 'left' | 'right', delta: number): void {
    this.jumpMenu = null;
    if (side === 'right') {
      const next = this.shiftedMonth(this.rightDisplayYear, this.rightDisplayMonth, delta);
      this.rightDisplayYear = next.year;
      this.rightDisplayMonth = next.month;
      return;
    }
    const next = this.shiftedMonth(this.displayYear, this.displayMonth, delta);
    this.displayYear = next.year;
    this.displayMonth = next.month;
  }

  private shiftedMonth(year: number, month: number, delta: number): { year: number; month: number } {
    const date = new Date(year, month - 1 + delta, 1);
    return { year: date.getFullYear(), month: date.getMonth() + 1 };
  }

  /** Right calendar's visible month (independent of the left calendar). */
  get adjacentMonth(): { year: number; month: number } {
    return { year: this.rightDisplayYear, month: this.rightDisplayMonth };
  }

  private buildWeeks(year: number, month: number): DayCell[][] {
    const firstWeekday = new Date(year, month - 1, 1).getDay();
    const daysInMonth = new Date(year, month, 0).getDate();

    const cells: DayCell[] = [];
    for (let i = 0; i < firstWeekday; i++) {
      cells.push(null);
    }
    for (let day = 1; day <= daysInMonth; day++) {
      cells.push({ year, month, day });
    }
    while (cells.length % 7 !== 0) {
      cells.push(null);
    }

    const weeks: DayCell[][] = [];
    for (let i = 0; i < cells.length; i += 7) {
      weeks.push(cells.slice(i, i + 7));
    }
    return weeks;
  }

  private formatMonthLabel(year: number, month: number): string {
    return formatDate(new Date(year, month - 1, 1), 'MMMM y', 'en-US');
  }

  private formatDate(date: NgbDateStruct): string {
    return formatDate(new Date(date.year, date.month - 1, date.day), 'dd/MM/yyyy', 'en-AU');
  }

  private equals(a: NgbDateStruct, b: NgbDateStruct): boolean {
    return a.year === b.year && a.month === b.month && a.day === b.day;
  }

  /** Encodes a date as a sortable integer (yyyymmdd) for range comparisons. */
  private toComparable(date: NgbDateStruct): number {
    return date.year * 10000 + date.month * 100 + date.day;
  }
}
